import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { Card } from '../../components/ui/Card';
import { FavoriteButton } from '../../components/ui/FavoriteButton';
import { RepostButton } from '../../components/ui/RepostButton';
import { AddToPlaylist } from '../library/AddToPlaylist';
import { copy } from '../../config/copy';
import { api } from '../../lib/api';
import { usePlayer, type PlayableTrack } from '../../stores/player';

/**
 * The track shape the server sends, plus what the rewind added to it.
 *
 * `PlayableTrack` is what the player takes, and the endpoint already returns a
 * full track shape - so this extends it rather than describing a second one
 * that would drift.
 */
interface Rewind extends PlayableTrack {
  owner: { username: string; display_name: string };
  favorited: boolean;
  reposted: boolean;
  repost_count: number;
  caption: string | null;
  rewound_at: string;
}

/**
 * What this person has passed on.
 *
 * ## Why a rewind needed somewhere to live
 *
 * It already did three things - it entered every follower's timeline, it
 * counted three times a play in the trending score, and it moved a badge - and
 * all three were invisible from the outside. So the act read as a bookmark
 * nobody else could see, which is not a reason for anybody to do it twice.
 *
 * This is the part that makes it a public act with an address: here is what
 * they made, and directly under it, here is what they rate.
 *
 * ## The caption is the reason this is not a list of favourites
 *
 * A favourite is private appreciation. A rewind with "this bridge is unreal" on
 * it is a recommendation with a name attached, and it is the only thing here
 * that a play count cannot say. So the caption is given its own line rather
 * than a tooltip, and a rewind without one still renders - people press the
 * button faster than they write.
 *
 * ## Empty means absent
 *
 * `ProfileLayout` drops a section whose body is null, so somebody who has never
 * rewound anything gets no heading rather than a heading over nothing.
 */
export function Rewinds({ username }: { username: string }) {
  const play = usePlayer((state) => state.play);

  const { data, isPending } = useQuery({
    queryKey: ['rewinds', username],
    queryFn: () => api.get<{ items: Rewind[] }>(`/users/${username}/rewinds`),
  });

  // Nothing yet, or nothing to say yet - either way there is no section.
  if (isPending || !data || data.items.length === 0) return null;

  return (
    <section aria-labelledby="profile-rewinds">
      <h2
        id="profile-rewinds"
        className="border-b border-line pb-3 text-lg font-bold tracking-tight"
      >
        {copy['noun.repost.plural']}
      </h2>

      <div className="mt-4 flex flex-col gap-3" data-rewinds>
        {data.items.map((track) => (
          <Card key={track.id} className="flex flex-col gap-2 p-3" data-rewind={track.id}>
            <div className="flex items-center gap-3">
              {/*
                The whole list is the queue, so pressing one plays on into the
                rest - the same as the track list above it. A rewind list you
                have to click through one at a time is a worse list.
              */}
              <button
                type="button"
                aria-label={`Play ${track.title}`}
                data-rewind-play={track.id}
                onClick={() => void play(track, data.items, 'profile')}
                className="h-11 w-11 shrink-0 overflow-hidden rounded-[var(--radius-control)] bg-blank"
              >
                {(track.artwork_small ?? track.artwork) && (
                  <img
                    src={track.artwork_small ?? track.artwork ?? undefined}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                )}
              </button>

              <div className="min-w-0 flex-1">
                <Link
                  to={`/tracks/${track.id}`}
                  className="block truncate text-sm font-medium text-ink hover:underline"
                >
                  {track.title}
                </Link>
                <Link
                  to={`/${track.owner.username}`}
                  className="block truncate font-mono text-[11px] text-soft hover:underline"
                >
                  {track.owner.display_name}
                </Link>
              </div>

              {/*
                The same controls the track carries everywhere else. A list of
                recommendations you cannot act on from is a list you leave.
              */}
              <div className="flex shrink-0 items-center">
                <FavoriteButton trackId={track.id} favorited={track.favorited} size="sm" />
                <RepostButton
                  title={track.title}
                  artist={track.owner.display_name}
                  trackId={track.id}
                  reposted={track.reposted}
                  repostCount={track.repost_count}
                  size="sm"
                />
                <AddToPlaylist trackId={track.id} title={track.title} size="sm" />
              </div>
            </div>

            {track.caption && (
              <p className="border-l-2 border-line pl-3 text-sm leading-relaxed text-soft">
                {track.caption}
              </p>
            )}
          </Card>
        ))}
      </div>
    </section>
  );
}
